import React, { Component } from 'react';
import { getOppositeOrient } from '@src/slide/application/service/snap-line-service/util/movement';
import { MarginSnapLineType } from '@src/slide/application/service/snap-line-service/interface/line.interface';
import { EdgeType, Orient } from '@src/slide/application/service/snap-line-service/interface/orient.interface';
import { RefSourceType } from '@src/slide/application/service/snap-line-service/interface/ref.interface';
import { ArrowLine } from '@src/slide/application/service/snap-line-service/render/components/arrow-line';
import { ExtendLine } from '@src/slide/application/service/snap-line-service/render/components/extend-line';
import flatten from 'lodash/flatten';
import sortBy from 'lodash/sortBy';
import type {
    IRefFrame,
    MarginRefGroup,
    SymmetryRefGroup,
} from '@src/slide/application/service/snap-line-service/interface/ref.interface';
import type { IArrowLineProps } from '@src/slide/application/service/snap-line-service/render/components/arrow-line';
import type { IExtendLineProps } from '@src/slide/application/service/snap-line-service/render/components/extend-line';
import type { IFrame } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import type {
    IActiveMarginSnapGridLine,
} from '@src/slide/application/service/snap-line-service/interface/line.interface';
import { IRect } from '@src/slide/infrastructure/module/independent/graphic/rect/rect';
import { IBaseUIProps } from '@src/slide/application/service/snap-line-service/interface/render.interface';

interface MarginSnapLineProps extends IBaseUIProps {
    snapLines: IActiveMarginSnapGridLine[];
    color?: string
    displayPadding: number;
    arrowSize: Pick<IRect, 'width' | 'height'>;
}

/**
 * 间距参考线
 * 会在所有间距相等的元素间，渲染辅助线 和 双箭头参考线
 */
export class MarginSnapLines extends Component<MarginSnapLineProps> {
    public render() {
        const { snapLines } = this.props;

        const slideSymmetrySnapLines = snapLines.filter(snapLine => this.isSlideSymmetrySnapLines(snapLine));
        const marginSnapLines = snapLines.filter(snapLine => !this.isSlideSymmetrySnapLines(snapLine));

        return <>
            {this.renderSlideSymmetrySnapLines(slideSymmetrySnapLines)}
            {this.renderMarginSnapLines(marginSnapLines)}
        </>;
    }

    private get displayPadding() {
        return this.props.displayPadding;
    }

    private renderSlideSymmetrySnapLines(slideSymmetrySnapLines: IActiveMarginSnapGridLine[]) {
        return slideSymmetrySnapLines.map((snapLine, index) => {
            const symmetryFrames = this.getSlideSymmetryMarginSnapLineFrames(snapLine);
            const { arrowLines, extendLines } = this.getSlideSymmetryMarginSnapLineGroupProps(symmetryFrames, snapLine);
            return <MarginSnapLineGroup key={index} arrowLines={arrowLines} extendLines={extendLines}/>;
        });
    }

    private renderMarginSnapLines(marginSnapLines: IActiveMarginSnapGridLine[]) {
        const propsList = flatten(marginSnapLines.map((snapLine) => {
            const groupedFrames = this.getElementMarginSnapLineGroupFrames(snapLine);
            return groupedFrames.map(frames => this.getElementMarginSnapLineGroupProps(frames, snapLine));
        }));

        const filterOverlappedMarginSnapLineGroup = this.filterOverlappedMarginSnapLineGroup(propsList);
        return <>
            {
                filterOverlappedMarginSnapLineGroup.map((marginGroupProps, groupIndex) => {
                    const { arrowLines, extendLines } = marginGroupProps;
                    return <MarginSnapLineGroup key={groupIndex} arrowLines={arrowLines} extendLines={extendLines}/>;
                })
            }
        </>;
    }

    // 重叠的marginSnapLine 只显示最外侧的
    private filterOverlappedMarginSnapLineGroup(propsList: MarginSnapLineGroupProps[]): MarginSnapLineGroupProps[] {
        const groupsMap = new Map<string, MarginSnapLineGroupProps>();

        propsList.forEach((group) => {
            const [arrowLineProps] = group.arrowLines;
            const key = `${arrowLineProps.orient}-${arrowLineProps.range.map(r => r.toFixed(2)).join('-')}`;
            const arrowLinePosition = groupsMap.get(key)?.arrowLines[0].position;
            if (!groupsMap.has(key) || (arrowLinePosition && arrowLinePosition < arrowLineProps.position)) {
                groupsMap.set(key, group);
            }
        });

        return Array.from(groupsMap.values());
    }


    // 是否为页面对称参考线
    private isSlideSymmetrySnapLines(snapLine: IActiveMarginSnapGridLine) {
        const { source } = snapLine;
        return source === RefSourceType.SLIDE;
    }

    private getElementMarginSnapLineGroupFrames(snapLine: IActiveMarginSnapGridLine): IFrame[][] {
        const {  marginType } = snapLine;

        if (marginType === MarginSnapLineType.BETWEEN) {
            return this.getBetweenReferMarginSnapLineGroupedFrames(snapLine);
        }

        return this.getExtendMarginSnapLineGroupedFrames(snapLine);
    }

    private getExtendMarginSnapLineGroupedFrames(snapLine: IActiveMarginSnapGridLine) {
        const { equalMarginRefGroup } = snapLine;
        const groupedFrames = (equalMarginRefGroup as MarginRefGroup)
            .map(group => group.map(ref => ref.outerFrameOnSlide));
        groupedFrames.push(this.getOperationElementMarginGroup(snapLine));

        return groupedFrames;
    }

    private getBetweenReferMarginSnapLineGroupedFrames(snapLine: IActiveMarginSnapGridLine) {
        const { refElements } = snapLine;
        const [frontRef, behindRef] = refElements;
        const frontRefFrame = frontRef.outerFrameOnSlide;
        const behindRefFrame = behindRef.outerFrameOnSlide;

        const elementFrame = this.getOperationElementFrame(snapLine);
        return [
            [frontRefFrame, elementFrame],
            [elementFrame, behindRefFrame],
        ];
    }

    private getSlideSymmetryMarginSnapLineFrames(snapLine: IActiveMarginSnapGridLine): IFrame[] {
        const { equalMarginRefGroup } = snapLine;
        const groupedFrames: IFrame[] = (equalMarginRefGroup as SymmetryRefGroup)
            .map(ref => ref.outerFrameOnSlide);
        groupedFrames.push(this.getOperationElementFrame(snapLine));

        return groupedFrames;
    }

    private getElementMarginSnapLineGroupProps(
        frames: IFrame[],
        snapLine: IActiveMarginSnapGridLine,
    ): MarginSnapLineGroupProps {
        const { orient } = snapLine;
        const isVertical = this.isVertical(orient);
        const edgePosition = Math.max(...frames.map(frame => (isVertical
            ? this.getFrameBottom(frame) : this.getFrameRight(frame))));
        const extendLineEdge = edgePosition + this.displayPadding;

        const extendLines: IExtendLineProps[] = this.orderFramesWithOrient(frames, orient).map(((frame, index) => {
            let position: number;
            if (isVertical) {
                position = index === 0 ? this.getFrameRight(frame) : frame.left;
            } else {
                position = index === 0 ? this.getFrameBottom(frame) : frame.top;
            }

            return {
                orient,
                range: [this.isVertical(orient) ? frame.top : frame.left, extendLineEdge],
                position,
                ...this.extendLineStyleProps,
            };
        }));

        const arrowLines: IArrowLineProps[] = [{
            orient: getOppositeOrient(orient),
            range: sortBy(extendLines.map(line => line.position)) as [number, number],
            position: extendLineEdge,
            ...this.arrowLineStyleSheetProps,
        }];

        return { extendLines, arrowLines };
    }

    private getSlideSymmetryMarginSnapLineGroupProps(
        frames: IFrame[],
        snapLine: IActiveMarginSnapGridLine,
    ): MarginSnapLineGroupProps {
        const { orient, refElements } = snapLine;
        const slideRef = refElements.find(ref => ref.source === RefSourceType.SLIDE);
        if (!slideRef) return { extendLines: [], arrowLines: [] };

        const slideRefFrame = slideRef.outerFrameOnSlide;
        const extendLines: IExtendLineProps[] = [];
        const arrowLines: IArrowLineProps[] = [];

        // 每一个 frame 都有一个辅助线和双箭头
        frames.map((frameInfo) => {
            const frame = this.getRefFrame(frameInfo);
            if (this.isVertical(orient)) {
                const isRefLeftToBaseLine = frame.right < slideRefFrame.left;
                const IExtendLineProps: IExtendLineProps = {
                    orient,
                    range: [frame.top, frame.bottom + this.displayPadding],
                    position: isRefLeftToBaseLine ? frame.right : frame.left,
                    ...this.extendLineStyleProps,
                };
                extendLines.push(IExtendLineProps);
                arrowLines.push({
                    orient: getOppositeOrient(orient),
                    range: [IExtendLineProps.position, slideRefFrame.left],
                    position: IExtendLineProps.range[1],
                    ...this.arrowLineStyleSheetProps,
                });
            } else {
                const isRefAboveBaseLine = frame.bottom < slideRefFrame.top;
                const IExtendLineProps: IExtendLineProps = {
                    orient,
                    range: [frame.left, frame.right + this.displayPadding],
                    position: isRefAboveBaseLine ? frame.bottom : frame.top,
                    ...this.extendLineStyleProps,
                };
                extendLines.push(IExtendLineProps);
                arrowLines.push({
                    orient: getOppositeOrient(orient),
                    range: [IExtendLineProps.position, slideRefFrame.top],
                    position: IExtendLineProps.range[1],
                    ...this.arrowLineStyleSheetProps,
                });
            }
        });

        // 加入 slideRef 辅助线
        extendLines.push({
            orient,
            range: this.isVertical(orient)
                ? [slideRefFrame.top, this.getFrameBottom(slideRefFrame)]
                : [slideRefFrame.left, this.getFrameRight(slideRefFrame)],
            position: this.isVertical(orient) ? slideRefFrame.left : slideRefFrame.top,
            ...this.extendLineStyleProps,
        });

        return { extendLines, arrowLines };
    }

    private get extendLineStyleProps() {
        return {
            scale: this.props.scale,
            strokeWidth: this.props.strokeWidth,
            strokeDash: this.props.strokeDash,
        };
    }

    private get arrowLineStyleSheetProps() {
        return {
            scale: this.props.scale,
            strokeWidth: this.props.strokeWidth,
            arrowSize: this.props.arrowSize,
            strokeDash: this.props.strokeDash,
        };
    }

    private orderFramesWithOrient(frames: IFrame[], snapLineOrient: Orient) {
        return sortBy(frames, [(frame) => {
            // 参考线的方向与元素垂直，如果是水平的，则元素是以竖直排列的
            if (snapLineOrient === Orient.HORIZONTAL) {
                return frame.top || 0;
            }
            return frame.left || 0;
        }]);
    }

    private getOperationElementMarginGroup(snapLine: IActiveMarginSnapGridLine) {
        const { refElements, orient, adsorbEdge, frame, position } = snapLine;
        const [frontRef, behindRef] = refElements;
        if (this.isVertical(orient)) {
            const isAbsorbLeftEdge = adsorbEdge === EdgeType.LEFT;
            if (isAbsorbLeftEdge) {
                return [behindRef.outerFrameOnSlide, this.getOperationElementFrame(snapLine)];
            }

            return [this.getOperationElementFrame(snapLine), frontRef.outerFrameOnSlide];
        }

        const isAbsorbTopEdge = adsorbEdge === EdgeType.TOP;
        if (isAbsorbTopEdge) {
            return [behindRef.outerFrameOnSlide, this.getOperationElementFrame(snapLine)];
        }

        return [this.getOperationElementFrame(snapLine), frontRef.outerFrameOnSlide];
    }

    private getOperationElementFrame(snapLine: IActiveMarginSnapGridLine) {
        return snapLine.frameOnSlide as IFrame;
    }

    private isVertical(orient: Orient) {
        return orient === Orient.VERTICAL;
    }

    private getRefFrame(frame: IFrame): IRefFrame {
        return {
            ...frame,
            right: frame.left + frame.width,
            bottom: frame.top + frame.height,
        };
    }

    private getFrameBottom(frame: IFrame) {
        return frame.top + frame.height;
    }
    private getFrameRight(frame: IFrame) {
        return frame.left + frame.width;
    }
}

interface MarginSnapLineGroupProps {
    extendLines: IExtendLineProps[];
    arrowLines: IArrowLineProps[];
}
/**
 * 两跟辅助线与一条双箭头的组合
 */
class MarginSnapLineGroup extends Component<MarginSnapLineGroupProps> {
    public render() {
        return <div className={'margin-snap-line'}>
            {this.props.extendLines.map((lineProps, index) => <ExtendLine {...lineProps} key={index}/>)}
            {this.props.arrowLines.map((IArrowLineProps, index) => <ArrowLine {...IArrowLineProps} key={index}/>)}
        </div>;
    }
}

